import React, { MouseEvent, useState } from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import LogoutIcon from '@mui/icons-material/Logout';
import FlagIcon from '@mui/icons-material/Flag';
import Box from '@mui/material/Box';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import PortraitIcon from '@mui/icons-material/Portrait';
import PersonIcon from '@mui/icons-material/Person';
import { styled } from '@mui/material/styles';
import ListItemText from '@mui/material/ListItemText';
import { FeatureFlagKeys } from '../../../../../../../shared/featureFlags/featureFlagKeys';
import { useFeatureFlag } from '../../../../../../../shared/featureFlags/useFeatureFlag';
import { Translate } from '../../../../../../../shared/LanguageService';
import { selectUserCountry } from '../../../../../../../reducers/userReducer';
import {
    impersonateCountry as _impersonateCountry,
    logout as _logout,
    unimpersonate,
    unImpersonateCountry as _unImpersonateCountry
} from '../../../../../../../actions/userActions';
import { Country, useImpersonatedCountryContext } from '../../HeadBar';
import { TranslatedLabels, UserRole } from '../../../../../../../shared/types';
import { ProfileModalProps } from '../ProfileContext';
import { useUserContext } from '../../../../../../../shared/context/user-context';
import { useTelemetryContext } from '../../../../../../../telemetry/telemetryContext';
import * as Features from '../../../../../../../shared/Features';
import history from '../../../../../../../shared/history';
import { getUserInfo } from '../../../../../../../telemetry/utils';
import SuccessSnackbar, {
    SuccessSnackbarProps
} from '../../../../../../../shared/feedback/SuccessSnackbar';
import PortfolioModal from '../../../../../../../features/photographerPortfolio/PortfolioModal';
import ProfileModal from './ProfileModal';

type ProfileContextMenuLabels = {
    impersonateCountry: string;
    impersonateCountrySuccess: string;
    unImpersonateCountry: string;
    unImpersonateCountrySuccess: string;
    unImpersonateUser: string;
    unImpersonateUserSuccess: string;
    profile: string;
    uploadProfileImage: string;
    logout: string;
    portfolio: string;
    logoutSuccess: string;
};

type TranslatedProfileContextMenuProps = {
    open: boolean;
    onClose: () => void;
    anchorElement: HTMLElement;
} & ProfileModalProps;

type ProfileContextMenuProps = {
    loadLanguage: (language: string) => void;
    labels: TranslatedLabels;
} & TranslatedProfileContextMenuProps;

type ReduxActions = {
    impersonateCountry: (country: Country) => void;
    logout: () => Promise<void>;
    unImpersonateCountry: () => void;
    unImpersonateUser: () => Promise<void>;
};

type ReduxProps = {
    countries: Country[];
};

type ChangeActiveCountrySubMenuProps = {
    open: boolean;
    onCloseOutside: () => void;
    onCloseSelect: (country: Country) => void;
    anchorElement: HTMLElement;
    countries: Country[];
};

type ImpersonateCountrySuccessSnackbarProps = {} & SuccessSnackbarProps;
type UnImpersonateCountrySuccessSnackbarProps = {} & SuccessSnackbarProps;
type UnImpersonateUserSuccessSnackbarProps = {} & SuccessSnackbarProps;

const MenuListItemText = styled(ListItemText)(() => ({
    '& .MuiTypography-root': {
        fontSize: '16px'
    }
}));

function ImpersonateCountrySuccessSnackbar({
    open,
    onClose,
    text
}: ImpersonateCountrySuccessSnackbarProps) {
    return <SuccessSnackbar open={open} onClose={onClose} text={text} />;
}

function UnImpersonateCountrySuccessSnackbar({
    open,
    onClose,
    text
}: UnImpersonateCountrySuccessSnackbarProps) {
    return <SuccessSnackbar open={open} onClose={onClose} text={text} />;
}

function UnImpersonateUserSuccessSnackbar({
    open,
    onClose,
    text
}: UnImpersonateUserSuccessSnackbarProps) {
    return <SuccessSnackbar open={open} onClose={onClose} text={text} />;
}

function ChangeActiveCountrySubMenu({
    open,
    onCloseOutside,
    onCloseSelect,
    anchorElement,
    countries
}: ChangeActiveCountrySubMenuProps) {
    return (
        <Menu
            open={open}
            onClose={onCloseOutside}
            anchorEl={anchorElement}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
            {countries.map((country) => (
                <MenuItem
                    key={country.id}
                    onClick={() => {
                        onCloseSelect(country);
                    }}
                >
                    <MenuListItemText>{country.name}</MenuListItemText>
                </MenuItem>
            ))}
        </Menu>
    );
}

function ProfileContextMenu({
    open,
    onClose,
    anchorElement,
    labels,
    unImpersonateUser,
    logout,
    countries,
    impersonateCountry,
    unImpersonateCountry,
    availableLanguages,
    availableTimeZones,
    userProfile,
    loadLanguage
}: ProfileContextMenuProps & ReduxProps & ReduxActions) {
    let [showChangeActiveCountrySubMenu, setShowChangeActiveCountrySubMenu] =
        useState<boolean>(false);
    let [showUnImpersonateUserSuccessSnackbar, setShowUnImpersonateUserSuccessSnackbar] =
        useState<boolean>(false);
    let [showImpersonateCountrySuccessSnackbar, setShowImpersonateCountrySuccessSnackbar] =
        useState<boolean>(false);
    let [showUnImpersonateCountrySuccessSnackbar, setShowUnImpersonateCountrySuccessSnackbar] =
        useState<boolean>(false);
    let [subMenuAnchorElement, setSubMenuAnchorElement] = useState<HTMLButtonElement | null>(null);
    let [showPortfolioModal, setShowPortfolioModal] = useState<boolean>(false);
    let [showProfileModal, setShowProfileModal] = useState<boolean>(false);

    let { user } = useUserContext();
    // @ts-ignore
    let { impersonatedCountry, setImpersonatedCountry } = useImpersonatedCountryContext();
    let { logger } = useTelemetryContext();

    let shouldShowImpersonateCountry = Features.hasFeature(Features.ImpersonateCountry, user);

    let hasProfileModalEnabled = useFeatureFlag(FeatureFlagKeys.ShowProfileModal, false);

    function handleClickChangeActiveCountry(event: MouseEvent<HTMLButtonElement>) {
        setSubMenuAnchorElement(event.currentTarget);
        setShowChangeActiveCountrySubMenu((oldValue) => !oldValue);
    }

    function handleCloseChangeActiveCountryMenuOutside() {
        setShowChangeActiveCountrySubMenu(false);
        setSubMenuAnchorElement(null);
    }

    function handleCloseChangeActiveCountryMenuSelect(country: Country) {
        setShowChangeActiveCountrySubMenu(false);
        setSubMenuAnchorElement(null);
        onClose();
        impersonateCountry(country);
        setImpersonatedCountry(country);
        setShowImpersonateCountrySuccessSnackbar(true);
        history.push('/');
    }

    function handleClickUnImpersonateUser() {
        onClose();
        unImpersonateUser()
            .then(() => {
                setShowUnImpersonateUserSuccessSnackbar(true);
                history.push('/');
            })
            .catch((error) => {
                let additionalInfo = {
                    origin: 'header.unImpersonateUser.click',
                    userInfo: getUserInfo(user)
                };
                logger.logException(error, additionalInfo);
            });
    }

    function handleClickUnImpersonateCountry() {
        onClose();
        unImpersonateCountry();
        setShowUnImpersonateCountrySuccessSnackbar(true);
        setImpersonatedCountry(null);
        history.push('/');
    }

    function handleCloseUnImpersonateUserSuccessSnackbar() {
        setShowUnImpersonateUserSuccessSnackbar(false);
    }

    function handleCloseImpersonateCountrySuccessSnackbar() {
        setShowImpersonateCountrySuccessSnackbar(false);
    }

    function handleCloseUnImpersonateCountrySuccessSnackbar() {
        setShowUnImpersonateCountrySuccessSnackbar(false);
    }

    function handleClickProfile() {
        onClose();
        setShowProfileModal(true);
    }

    function handleClickLogout() {
        onClose();
        logout().catch((error) => {
            let additionalInfo = {
                origin: 'header.logout.click',
                userInfo: getUserInfo(user)
            };
            logger.logException(error, additionalInfo);
        });
    }

    function handleClickPortfolio() {
        onClose();
        setShowPortfolioModal(true);
    }

    function handleClickProfileOld() {
        onClose();
        history.push('/profile');
    }

    return (
        <>
            <Menu open={open} onClose={onClose} anchorEl={anchorElement}>
                {user.details.isImpersonated ? (
                    <MenuItem onClick={handleClickUnImpersonateUser}>
                        <ListItemIcon>
                            <LogoutIcon fontSize="medium" />
                        </ListItemIcon>
                        <MenuListItemText>{labels.unImpersonateUser}</MenuListItemText>
                    </MenuItem>
                ) : null}
                {shouldShowImpersonateCountry && !impersonatedCountry ? (
                    <MenuItem onClick={handleClickChangeActiveCountry}>
                        <ListItemIcon>
                            <FlagIcon fontSize="medium" />
                        </ListItemIcon>
                        <MenuListItemText>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                {labels.impersonateCountry}
                                <NavigateNextIcon />
                            </Box>
                        </MenuListItemText>
                    </MenuItem>
                ) : null}
                {impersonatedCountry ? (
                    <MenuItem onClick={handleClickUnImpersonateCountry}>
                        <ListItemIcon>
                            <LogoutIcon fontSize="medium" />
                        </ListItemIcon>
                        <MenuListItemText>{labels.unImpersonateCountry}</MenuListItemText>
                    </MenuItem>
                ) : null}
                {user.details.role === UserRole.Photographer ? (
                    <MenuItem onClick={handleClickPortfolio}>
                        <ListItemIcon>
                            <PortraitIcon fontSize="medium" />
                        </ListItemIcon>
                        <MenuListItemText>{labels.portfolio}</MenuListItemText>
                    </MenuItem>
                ) : null}
                {hasProfileModalEnabled && !user.details.isImpersonated ? (
                    <MenuItem onClick={handleClickProfile}>
                        <ListItemIcon>
                            <PersonIcon fontSize="medium" />
                        </ListItemIcon>
                        <MenuListItemText>{labels.profile}</MenuListItemText>
                    </MenuItem>
                ) : null}
                {hasProfileModalEnabled && !user.details.isImpersonated ? (
                    <MenuItem onClick={handleClickProfileOld}>
                        <ListItemIcon>
                            <PersonIcon fontSize="medium" />
                        </ListItemIcon>
                        <MenuListItemText>{labels.uploadProfileImage}</MenuListItemText>
                    </MenuItem>
                ) : null}
                {!hasProfileModalEnabled && !user.details.isImpersonated ? (
                    <MenuItem onClick={handleClickProfileOld}>
                        <ListItemIcon>
                            <PersonIcon fontSize="medium" />
                        </ListItemIcon>
                        <MenuListItemText>{labels.profile}</MenuListItemText>
                    </MenuItem>
                ) : null}
                <MenuItem onClick={handleClickLogout}>
                    <ListItemIcon>
                        <LogoutIcon fontSize="medium" />
                    </ListItemIcon>
                    <MenuListItemText>{labels.logout}</MenuListItemText>
                </MenuItem>
            </Menu>
            <ChangeActiveCountrySubMenu
                open={showChangeActiveCountrySubMenu}
                onCloseOutside={handleCloseChangeActiveCountryMenuOutside}
                onCloseSelect={handleCloseChangeActiveCountryMenuSelect}
                anchorElement={subMenuAnchorElement}
                countries={countries}
            />
            <ImpersonateCountrySuccessSnackbar
                open={showImpersonateCountrySuccessSnackbar}
                onClose={handleCloseImpersonateCountrySuccessSnackbar}
                text={labels.impersonateCountrySuccess}
            />
            <UnImpersonateCountrySuccessSnackbar
                open={showUnImpersonateCountrySuccessSnackbar}
                onClose={handleCloseUnImpersonateCountrySuccessSnackbar}
                text={labels.unImpersonateCountrySuccess}
            />
            <UnImpersonateUserSuccessSnackbar
                open={showUnImpersonateUserSuccessSnackbar}
                onClose={handleCloseUnImpersonateUserSuccessSnackbar}
                text={labels.unImpersonateUserSuccess}
            />
            <PortfolioModal
                photographer={{ name: user.details.name, id: user.details.id }}
                show={showPortfolioModal}
                onClose={() => {
                    setShowPortfolioModal(false);
                }}
            />
            {!user.details.isImpersonated ? (
                <ProfileModal
                    open={showProfileModal}
                    onClose={() => setShowProfileModal(false)}
                    userProfile={userProfile}
                    availableLanguages={availableLanguages}
                    availableTimeZones={availableTimeZones}
                    loadLanguage={loadLanguage}
                />
            ) : null}
        </>
    );
}

function mapStateToProps(state): ReduxProps {
    let userCountryId = selectUserCountry(state);

    return {
        countries: state.systemInfo.countries
            .filter((country) => country.id !== userCountryId)
            .map(({ id, name }) => ({
                id,
                name: Translate(name)
            }))
    };
}

function mapDispatchToProps(dispatch: Dispatch<unknown>): ReduxActions {
    return {
        impersonateCountry: (country) => {
            dispatch(_impersonateCountry(country));
        },
        logout: () => dispatch(_logout()),
        unImpersonateCountry: () => {
            dispatch(_unImpersonateCountry());
        },
        unImpersonateUser: () => dispatch(unimpersonate())
    };
}

const ConnectedProfileContextMenu = connect<ReduxProps, ReduxActions, ProfileContextMenuProps>(
    mapStateToProps,
    mapDispatchToProps
)(ProfileContextMenu);

function TranslatedProfileContextMenu(props: TranslatedProfileContextMenuProps) {
    let labels: ProfileContextMenuLabels = {
        impersonateCountry: Translate('header/nav/impersonate-country'),
        impersonateCountrySuccess: Translate(
            'impersonation/notification/now-impersonating-country'
        ),
        unImpersonateCountry: Translate('header/button-label/leave-country-impersonation'),
        unImpersonateCountrySuccess: Translate(
            'impersonation/notification/left-country-impersonation'
        ),
        unImpersonateUser: Translate('header/button-label/leave-impersonation'),
        unImpersonateUserSuccess: Translate('impersonation/notification/left-impersonation'),
        profile: Translate('header/nav/profile'),
        uploadProfileImage: Translate('header/nav/upload-profile-image'),
        logout: Translate('header/nav/logout'),
        portfolio: Translate('header/nav/portfolio'),
        logoutSuccess: Translate('system/toastr/logged-out')
    };

    return <ConnectedProfileContextMenu {...props} labels={labels} />;
}

export default TranslatedProfileContextMenu;
